<template>
  <div class="pad">
    <svg
      ref="surface"
      class="pad__surface"
      :class="{ 'pad__surface--bucket': tool === 'fill' }"
      :viewBox="`0 0 ${CANVAS_UNITS} ${CANVAS_UNITS}`"
      preserveAspectRatio="xMidYMid meet"
      @pointerdown="start"
      @pointermove="move"
      @pointerup="end"
      @pointercancel="end"
      @pointerleave="end"
    >
      <rect class="pad__paper" x="0" y="0" :width="CANVAS_UNITS" :height="CANVAS_UNITS" />
      <template v-for="(stroke, i) in strokes" :key="i">
        <path
          v-if="isFill(stroke)"
          :d="fillPath(stroke)"
          :fill="stroke.c"
          stroke="none"
          shape-rendering="crispEdges"
        />
        <path
          v-else
          :d="strokePath(stroke)"
          :stroke="stroke.c"
          :stroke-width="stroke.w"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </template>
    </svg>

    <div v-if="!readOnly" class="pad__tools">
      <button
        v-for="c in COLORS"
        :key="c"
        class="pad__swatch"
        :class="{ 'pad__swatch--on': tool !== 'erase' && c === color }"
        :style="{ background: c }"
        :aria-label="`Colour ${c}`"
        @click="pick(c)"
      />
      <!--
        The colour picker (bug report, 2026-09-11: "would love a color
        picker"). The native input, dressed as one more swatch that shows
        whatever was last picked. A stroke's colour is any CSS colour string,
        so nothing downstream needs to know the palette grew.
      -->
      <label
        class="pad__swatch pad__swatch--custom"
        :class="{ 'pad__swatch--on': tool !== 'erase' && color === custom }"
        :style="{ background: custom }"
        aria-label="Pick any colour"
        title="Pick any colour"
      >
        <input type="color" :value="custom" @input="pickCustom($event.target.value)" />
      </label>
      <span class="pad__spacer" />
      <button
        v-for="w in WIDTHS"
        :key="w"
        class="pad__width"
        :class="{ 'pad__width--on': w === width }"
        :aria-label="`Line width ${w}`"
        @click="width = w"
      >
        <span :style="{ width: `${w / 2}px`, height: `${w / 2}px` }" />
      </button>
      <span class="pad__spacer" />
      <button
        class="pad__tool"
        :class="{ 'pad__tool--on': tool === 'erase' }"
        :aria-pressed="tool === 'erase'"
        @click="toggle('erase')"
      >Eraser</button>
      <button
        class="pad__tool"
        :class="{ 'pad__tool--on': tool === 'fill' }"
        :aria-pressed="tool === 'fill'"
        @click="toggle('fill')"
      >Fill</button>
      <button class="pad__undo" :disabled="!strokes.length" @click="undo">Undo</button>
      <button class="pad__undo" :disabled="!strokes.length" @click="clear">Clear</button>
    </div>
  </div>
</template>

<script>
import {
  CANVAS_UNITS, PAPER, toUnits, farEnough, createStroke, strokePath, fillPath, isFill,
  trimToFit, isTooBig,
} from '@/engine/strokes';
import { floodFill } from '@/engine/fill';

// Brown joined the row on request (bug report, 2026-09-11: "a brown color").
const COLORS = ['#1b1b1b', '#d94f3d', '#e07b28', '#8b5a2b', '#2f7ed8', '#3aa757', '#e8b93a', '#8d5bd6'];
const WIDTHS = [8, 16, 28];

export default {
  name: 'DrawingPad',
  props: {
    modelValue: { type: Array, default: () => [] },
    readOnly: { type: Boolean, default: false },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      CANVAS_UNITS,
      COLORS,
      WIDTHS,
      color: COLORS[0],
      custom: '#ff69b4',
      width: WIDTHS[1],
      // 'draw' | 'erase' | 'fill'. Erasing is drawing in paper colour; filling
      // replaces the stroke on pointerdown with a flood at that point.
      tool: 'draw',
      drawing: false,
      // The live drawing is owned HERE, not read back off the prop.
      //
      // Reading `this.modelValue` and emitting `[...that, point]` looks
      // tidier and silently loses input: Vue flushes prop updates
      // asynchronously, so every pointer event that fires before the next
      // flush builds from a stale array and overwrites its predecessors.
      // pointermove genuinely does fire several times per frame on a 120Hz
      // screen, and coalesced events make it worse.
      //
      // Caught in the browser: two strokes drawn in one tick left exactly
      // one on the pad.
      list: [...(this.modelValue || [])],
    };
  },
  computed: {
    strokes() { return this.list; },
  },
  watch: {
    // Adopt an external reset (a new page, or the parent clearing us), but
    // ignore the echo of our own emit, which would otherwise stamp on a
    // stroke in progress.
    modelValue(next) {
      const incoming = next || [];
      if (incoming === this.list) return;
      if (incoming.length === 0 && this.list.length > 0 && !this.drawing) this.list = [];
      else if (incoming.length !== this.list.length && !this.drawing) this.list = [...incoming];
    },
  },
  methods: {
    strokePath,
    fillPath,
    isFill,
    rect() { return this.$refs.surface?.getBoundingClientRect(); },
    pick(c) { this.color = c; if (this.tool === 'erase') this.tool = 'draw'; },
    pickCustom(value) { this.custom = value; this.pick(value); },
    toggle(tool) { this.tool = this.tool === tool ? 'draw' : tool; },
    // What a new mark is painted in: paper for the eraser, else the colour.
    inkColor() { return this.tool === 'erase' ? PAPER : this.color; },
    fillAt(event) {
      const [x, y] = toUnits(event.clientX, event.clientY, this.rect());
      const fill = floodFill(this.list, x, y, this.color);
      if (!fill) return;
      this.list = [...this.list, fill];
      if (isTooBig(this.list)) this.list = trimToFit(this.list);
      this.publish();
    },
    start(event) {
      if (this.readOnly) return;
      if (this.tool === 'fill') {
        event.preventDefault();
        this.fillAt(event);
        return;
      }
      // Capture, so a finger that slides off the pad mid-stroke keeps drawing
      // instead of the line stopping dead at the edge.
      //
      // In a try/catch, and the `?.` alone was not enough: setPointerCapture
      // THROWS NotFoundError when the pointer id isn't currently active, which
      // happens with synthetic events and with a pointer released between
      // dispatch and handling. An uncaught throw here aborts the handler
      // before the stroke is even created - i.e. drawing silently stops
      // working - and capture is a nicety, not a requirement.
      try {
        event.currentTarget.setPointerCapture?.(event.pointerId);
      } catch {
        // No capture; the stroke still draws, it just ends at the edge.
      }
      event.preventDefault();
      this.drawing = true;
      const stroke = createStroke(this.inkColor(), this.width);
      stroke.p.push(toUnits(event.clientX, event.clientY, this.rect()));
      this.list = [...this.list, stroke];
      this.publish();
    },
    move(event) {
      if (!this.drawing || this.readOnly) return;
      event.preventDefault();
      const stroke = this.list[this.list.length - 1];
      if (!stroke) return;
      const point = toUnits(event.clientX, event.clientY, this.rect());
      const last = stroke.p[stroke.p.length - 1];
      // Thin the stream as it arrives - see strokes.js. Doing it here rather
      // than on submit keeps the in-memory drawing small too, which matters
      // because every move re-renders the whole path list.
      if (last && !farEnough(last, point)) return;
      stroke.p.push(point);
      if (isTooBig(this.list)) this.list = trimToFit(this.list);
      this.publish();
    },
    end(event) {
      if (!this.drawing) return;
      this.drawing = false;
      // Always keep the last point, so a line ends where the finger lifted
      // rather than at the last thinned sample.
      const stroke = this.list[this.list.length - 1];
      if (stroke && event.clientX !== undefined) {
        const point = toUnits(event.clientX, event.clientY, this.rect());
        const last = stroke.p[stroke.p.length - 1];
        if (last && (last[0] !== point[0] || last[1] !== point[1])) {
          stroke.p.push(point);
          this.publish();
        }
      }
    },
    publish() { this.$emit('update:modelValue', this.list); },
    undo() { this.list = this.list.slice(0, -1); this.publish(); },
    clear() { this.list = []; this.publish(); },
  },
};
</script>
